<template>
  <v-container>
    <h1>{{ company.name }} Company Detail</h1>
    <v-row key="setting" class="my-4">
      <v-col cols="12">
        <div class="ml-8">
          <v-row v-for="item in settingItems" :key="item.key" align="center" class="my-2">
            <v-col cols="12">
              <v-text-field
                v-if="item.type === 'text'"
                v-model="companySetting[item.key]"
                :label="item.label"
                readonly
                hide-details
                @click="openEditDialog(item)"
                style="cursor: pointer;"
              ></v-text-field>
              <v-text-field
                v-if="item.type === 'list'"
                :value="companySetting[item.key].join(', ')"
                :label="item.label"
                readonly
                hide-details
                @click="openEditDialog(item)"
                style="cursor: pointer;"
              ></v-text-field>
              <v-select
                v-if="item.type === 'dropdown'"
                v-model="companySetting[item.key]"
                :items="source[item.key]"
                item-text="name"
                item-value="value"
                :label="item.label"
                readonly
                hide-details
                @click="openEditDialog(item)"
                style="cursor: pointer;"
              ></v-select>
            </v-col>
          </v-row>
          <v-dialog v-model="showEditDialog" max-width="600px">
            <v-card>
              <v-card-title>Edit {{ editFieldLabel }}</v-card-title>
              <v-card-text>
                <template v-if="editFieldInfo.type === 'text'">
                  <v-text-field
                    v-model="editValue"
                    :label="editFieldLabel"
                  ></v-text-field>
                </template>
                <template v-else-if="editFieldInfo.type === 'dropdown'">
                  <v-select
                    v-model="editValue"
                    :items="source[editFieldInfo.key]"
                    item-text="name"
                    item-value="value"
                    :label="editFieldLabel"
                  ></v-select>
                </template>
                <template v-else>
                  <v-combobox
                    v-model="editValue"
                    :label="editFieldLabel"
                    multiple
                    chips
                    deletable-chips
                  ></v-combobox>
                </template>
              </v-card-text>
              <v-card-actions>
                <v-spacer></v-spacer>
                <v-btn color="secondary" @click="showEditDialog = false">Cancel</v-btn>
                <v-btn color="primary" @click="saveField">Save</v-btn>
              </v-card-actions>
            </v-card>
          </v-dialog>
        </div>
      </v-col>
    </v-row>
    <v-row v-for="category in categories" :key="category.key" class="my-4">
      <v-col cols="12">
        <h2 class="mb-2 d-flex align-center">
          {{ category.name }}
          <v-btn
            v-if="checkCategoryButtonExist(category, 'start')"
            color="primary"
            fab
            x-small
            class="ml-4"
            @click="startNewTask(category)"
          >
            <v-icon>mdi-plus</v-icon>
          </v-btn>
          <v-btn
            v-if="checkCategoryButtonExist(category, 'startWithPassword')"
            color="primary"
            fab
            x-small
            class="ml-4"
            @click="openPasswordDialog(category)"
          >
            <v-icon>mdi-plus</v-icon>
          </v-btn>
          <v-btn
            v-if="checkCategoryButtonExist(category, 'add')"
            color="primary"
            fab
            x-small
            class="ml-4"
            @click="openUploadDialog(category)"
          >
            <v-icon>mdi-plus</v-icon>
          </v-btn>
          <v-btn
            v-if="checkCategoryButtonExist(category, 'restart')"
            color="primary"
            fab
            x-small
            class="ml-4"
            @click="restartCategoryAllTask(category)"
          >
            <v-icon>mdi-replay</v-icon>
          </v-btn>
          <v-btn
            v-if="checkCategoryButtonExist(category, 'summary')"
            color="primary"
            fab
            x-small
            class="ml-4"
            :href="downloadSummaryUrl(category)"
          >
            <v-icon>mdi-finance</v-icon>
          </v-btn>
        </h2>
        <div class="ml-8">
          <v-data-table
            :headers="getCategoryHeaders(category)"
            :items="categoryItems(category)"
            :items-per-page="-1"
            :hide-default-footer="true"
          >
            <template v-slot:item.doc_name="{ item }">
              {{ item.displayName }}
            </template>
            <template v-slot:item.progress_step="{ item }">
              <task-step-progress
                v-if="category.key === 'company_check'"
                :progress-step="item.progress_step"
                :steps="companyCheckProgressStep"
                class="mx-2"
              ></task-step-progress>
              <task-step-progress
                v-else
                :progress-step="item.progress_step"
                :steps="progressStepList"
                class="mx-2"
              ></task-step-progress>
            </template>
            <template v-slot:item.status="{ item }">
              {{ formatStatus(item?.progress_step?.displayStatus || item.status) }}
            </template>      
            <template v-slot:item.reextract="{ item }">
              <v-btn
                color="primary"
                small
                @click="restartIndivTask(item.id, category)"
                :disabled="!['completed', 'validate failed', 'analysis failed', 'fetch_failed'].includes((item?.progress_step?.displayStatus ?? '').toLowerCase())"
              >
                Re-extract
              </v-btn>
            </template>
            <template v-slot:item.review="{ item }">
              <v-btn
                color="primary"
                small
                @click="editTaskData(category, item.id, item.unique_id)"
                :disabled="!['completed', 'validate failed'].includes((item?.progress_step?.displayStatus ?? '').toLowerCase())"
              >
                Review
              </v-btn>
            </template>
            <template v-slot:item.show_result="{ item }">
              <v-btn
                color="primary"
                small
                @click="showResult(category, item.id, item.unique_id)"
                :disabled="(item?.progress_step?.displayStatus ?? '').toLowerCase() != 'completed'"
              >
                Result
              </v-btn>
            </template>
            <template v-slot:item.download="{ item }">
              <v-btn
                color="primary"
                small
                :href="downloadTaskOutput(category, item.id)"
                :disabled="(item?.progress_step?.displayStatus ?? item.status).toLowerCase() != 'completed'"
              >
                Download
              </v-btn>
            </template>
          </v-data-table>
        </div>
      </v-col>
    </v-row>
    <v-dialog v-model="showUploadDialog" max-width="600px">
      <v-card>
        <v-card-title>Upload Documents - {{ currentCategoryName }}</v-card-title>
        <v-card-text>
          <v-file-input
            v-model="uploadFiles"
            label="Select documents"
            multiple
            accept=".pdf,.doc,.docx,.xlsx,.xls"
            chips
            show-size
            :rules="fileRules"
          ></v-file-input>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="secondary" @click="showUploadDialog = false">Cancel</v-btn>
          <v-btn color="primary" @click="uploadDocuments" :disabled="!uploadFiles || uploadFiles.length === 0">Upload</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog v-model="showPasswordDialog" max-width="600px">
      <v-card>
        <v-card-title>Enter Password</v-card-title>
        <v-card-text>
          <v-text-field
            v-model="passwordInput"
            label="Password"
            type="password"
            :rules="[v => !!v || 'Password is required']"
          ></v-text-field>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="secondary" @click="showPasswordDialog = false">Cancel</v-btn>
          <v-btn 
            color="primary" 
            @click="submitPassword"
            :disabled="!passwordInput"
          >Submit</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import axios from 'axios'
import TaskStepProgress from '@/components/TaskStepProgress';
import { progressStepList, updateTaskProgressStep } from '@/utils/taskProgressUtils';

export default {
  components: {
    'task-step-progress': TaskStepProgress
  },
  data() {
    return {
      companyId: null,
      company: {
        name: '',
        tasks: {},
      },
      loading: false,
      showReviewColumn: this.$route.query.show_review != null,
      progressStepList: progressStepList,
      tasks: {},
      source: {
        region: [], // Store region list from API
      },
      companySetting: {
        region: '',  // Add region field
        company_unique_number: '',
        company_unique_number_2: '',
        client_list: [],
        supplier_list: []
      },
      companyCheckProgressStep: [
        "Processing", "Awaiting", "Completed"
      ],
      settingItems: [
        { key: 'region', label: 'Region', type: 'dropdown' },
        { key: 'company_unique_number', label: 'BR Number / Company Unique Number', type: 'text' },
        { key: 'company_unique_number_2', label: 'CI Number / Company Unique Number 2', type: 'text' },
        { key: 'client_list', label: 'Client List', type: 'list' },
        { key: 'supplier_list', label: 'Supplier List', type: 'list' }
      ],
      editFieldInfo: '',
      editValue: null,
      showEditDialog: false,
      showUploadDialog: false,
      showPasswordDialog: false,
      passwordInput: '',
      currentCategory: '',
      uploadFiles: null,
      categories: [
        { name: 'Credit Assessment Report', key: 'credit_assessment' },
        { name: 'Company Background Check', key: 'company_check', buttons: ["startWithPassword"], companySetting: ["region", "company_name", "company_unique_number"] },
        { name: 'Audit Report', key: 'audit_report', buttons: ["add"] },
        { name: 'Bank Statement', key: 'bank_statement', buttons: ["add", "restart", "summary"], companySetting: ["client_list", "supplier_list"] },
        { name: 'Financial Document', key: 'financial_document', buttons: ["add", "summary"] }
      ],
      updateProgressInterval: null, // Store the interval ID
      fileRules: [
        files => !files || files.every(file => file.size < 10 * 1024 * 1024) || 'Each file must be less than 10MB!',
        files => !files || files.length <= 10 || 'Maximum 10 files allowed!'
      ],
      fetchTimer: null
    }
  },
  computed: {
    editFieldLabel() {
      const item = this.settingItems.find(item => item.key === this.editFieldInfo.key)
      return item ? item.label : ''
    },
    currentCategoryName() {
      return this.currentCategory?.name ? this.currentCategory.name : ''
    },
  },
  methods: {
    async fetchRegions() {
      try {
        const response = await this.$http.get('/api/company/region_list');
        this.source.region = response.data; // Assuming the API returns [{name, code}, ...]
      } catch (error) {
        console.error('Error fetching regions:', error);
        alert('Failed to fetch region list');
      }
    },
    checkCategoryButtonExist(category, button) {
      const buttons = category?.buttons ?? []
      return buttons.includes(button)
    },
    scheduleNextFetch(time) {
      this.fetchTimer = setTimeout(async () => {
        await this.fetchCompany();
        this.scheduleNextFetch(time);
      }, time);
    },
    updateTaskListProgressStep() {
      for (const type in this.company.tasks) {
        const cur_type_task_list = this.company.tasks[type]
        if (type == "company_check") {
          for (const task of cur_type_task_list) {
            let progress_step = {
              step: 0,
              isProcessing: true,
              displayStatus: task.status,
              currentStepColor: '',
              activeIconClass: '',
            };
            if (task.status === 'fetch_failed') {
              progress_step.currentStepColor = 'red';
              progress_step.activeIconClass = 'fa fa-times';
              progress_step.isProcessing = false;
              progress_step.step = 2;
            }
            else {
              progress_step.step = this.companyCheckProgressStep.findIndex(item => item.toLowerCase() === task.status.toLowerCase())
              if (task.status === 'completed') {
                progress_step.currentStepColor = 'green';
                progress_step.isProcessing = false;
              }
            }
            task.progress_step = progress_step
          }
        }
        else {
          for (const task of cur_type_task_list) {
            updateTaskProgressStep(task)
          }
        }
      }
    },
    updateTaskDisplayNameAndSort() {
      for (const type in this.company.tasks) {
        const cur_type_task_list = this.company.tasks[type]
        for (const task of cur_type_task_list) {
          let infoForCompany = {}
          if (task.info_for_company) {
            infoForCompany = JSON.parse(task.info_for_company)
          }
          task.displayName = task.unique_id
          if ((type == "audit_report") || (type == "financial_document")) {
            task.displayName = infoForCompany.year ?? task.unique_id
          }
          else if (type == "bank_statement") {
            let nameToDisplay = ""
            if (infoForCompany.name && infoForCompany.statement_date) {
              const date = new Date(infoForCompany.statement_date);
              if (!isNaN(date)) {
                const year = date.getFullYear();
                const month = String(date.getMonth() + 1).padStart(2, '0'); // Months are 0-based, so +1
                task.displayName = `${infoForCompany.name} ${year} ${month}`;
              }
            }
          }
          else if (type == "company_check") {
            task.displayName = this.formatTime(task.process_time, true)
            if (task.inputs) {
              let inputs = {}
              try {
                inputs = JSON.parse(task.inputs)
                if (inputs.datasource) {
                  task.displayName += ` ${inputs.datasource}`
                }
              } catch (error) {
                console.error('Error fetching companies:', error)
              }
            }
          }
        }
        cur_type_task_list.sort((a, b) => a.displayName.localeCompare(b.displayName))
        this.company.tasks[type] = cur_type_task_list
      }
      if (this.company.credit_assessment) {
        this.company.tasks["credit_assessment"] = [{
          displayName: "Credit Assessment",
          status: "COMPLETED",
        }]
      }
    },
    async fetchCompany() {
      this.loading = true
      try {
        const response = await this.$http.get(`/api/company/${this.companyId}`)
        this.company = response.data
        this.updateTaskListProgressStep()
        this.updateTaskDisplayNameAndSort()
        if (response.data.setting) {
          const setting = JSON.parse(response.data.setting)
          this.companySetting = {
            region: setting.region || '',
            company_unique_number: setting.company_unique_number || '',
            company_unique_number_2: setting.company_unique_number_2 || '',
            client_list: setting.client_list || [],
            supplier_list: setting.supplier_list || []
          }
        }
      } catch (error) {
        console.error('Error fetching companies:', error)
      }
      this.loading = false
    },
    async restartIndivTask(taskId, category=null) {
      this.loading = true
      let isSuccess = this.restartTask(taskId, category)
      if (isSuccess) {
        await this.fetchCompany()
        alert('Task restarted successfully')
      }
      else {
        alert('Failed to restart task')
      }
      this.loading = false
    },
    async restartTask(taskId, category=null) {
      try {
        const formData = new FormData()
        let companySetting = {}
        try {
          companySetting = JSON.parse(this.company.setting)
        } catch (error) {}
        let companySettingsForCategory = category?.companySetting ?? []
        let text_inputs = {}
        for (const settingForCategory of companySettingsForCategory) {
          let curSettingValue = null
          if (settingForCategory == "company_name") {
            curSettingValue = this.company.name
          }
          else {
            curSettingValue = companySetting[settingForCategory]
          }
          if (curSettingValue) {
            text_inputs[settingForCategory] = curSettingValue
          }
        }
        formData.append("text_inputs", JSON.stringify(text_inputs))
        await this.$http.post(`/api/tasks/${taskId}/restart`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        })

        return true
      } catch (error) {
        console.error('Error restarting task:', error)
        return false
      }
    },
    taskTypeToPascalCase(taskType) {
      return taskType.match(/[A-Z]{2,}(?=[A-Z][a-z]+[0-9]*|\b)|[A-Z]?[a-z]+[0-9]*|[A-Z]|[0-9]+/g)
            .map(x => x.slice(0, 1).toUpperCase() + x.slice(1).toLowerCase())
            .join('');
    },
    editTaskData(category, taskId, taskUniqueId) {
      let taskType = category.key
      this.$router.push({ name: `${this.taskTypeToPascalCase(taskType)}EditTaskData`, params: { id: taskId, uniqueId: taskUniqueId }, query: { show_review : this.$route.query.show_review } })
    },
    showResult(category, taskId, taskUniqueId) {
      let taskType = category.key
      this.$router.push({ name: `${this.taskTypeToPascalCase(taskType)}TaskResult`, params: { id: taskId, uniqueId: taskUniqueId} })
    },
    categoryItems(category) {
      return this.company.tasks[category.key] || []
    },
    openUploadDialog(category) {
      this.currentCategory = category
      this.uploadFiles = null
      this.showUploadDialog = true
    },
    async uploadDocuments() {
      if (!this.uploadFiles || this.uploadFiles.length === 0) return

      await this.fetchCompany()

      let taskType = this.currentCategory.key
      if (!taskType) {
        return
      }

      let curTaskList = this.company.tasks[taskType]
      let taskCount = 0
      if (curTaskList) {
        taskCount = curTaskList.length
      }

      this.loading = true
      try {
        let companySetting = {}
        try {
          companySetting = JSON.parse(this.company.setting)
        } catch (error) {
          console.error('Error reading company setting:', error)
        }
        for (const file of this.uploadFiles) {
          taskCount++
          const formData = new FormData()
          const docName = this.currentCategory.key.replace(/_/g, ' ')
          formData.append("unique_id", `${this.company.name} ${docName} ${taskCount}`)
          formData.append("company_id", this.companyId)
          formData.append("task_type", taskType)
          const companySettingsForCategory = this.currentCategory?.companySetting ?? []
          let text_inputs = {}
          for (const settingForCategory of companySettingsForCategory) {
            let curSettingValue = null
            if (settingForCategory == "company_name") {
              curSettingValue = this.company.name
            }
            else {
              curSettingValue = companySetting[settingForCategory]
            }
            if (curSettingValue) {
              text_inputs[settingForCategory] = curSettingValue
            }
          }
          formData.append("text_inputs", JSON.stringify(text_inputs))
          formData.append(this.currentCategory.key, file)

          await this.$http.post(`/api/tasks/${taskType}`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
          })
        }

        alert('Documents uploaded successfully')
      } catch (error) {
        console.error('Error uploading documents:', error)
        alert('Failed to upload documents')
      }
      this.loading = false
      this.showUploadDialog = false
      this.uploadFiles = null
      await this.fetchCompany()
    },
    openEditDialog(field) {
      this.editFieldInfo = field
      this.editValue = (field.type == "list")
        ? [...this.companySetting[field.key]] 
        : this.companySetting[field.key]
      this.showEditDialog = true
    },
    openPasswordDialog(category) {
      this.currentCategory = category
      this.passwordInput = ''
      this.showPasswordDialog = true
    },
    async submitPassword() {
      if (!this.passwordInput) return
      await this.startNewTask(this.currentCategory, this.passwordInput)
      this.showPasswordDialog = false
      this.passwordInput = ''
    },
    async startNewTask(category, password=null) {
      await this.fetchCompany()

      let taskType = category.key
      if (!taskType) {
        return
      }

      let curTaskList = this.company.tasks[taskType]
      let taskCount = 0
      if (curTaskList) {
        taskCount = curTaskList.length
      }

      this.loading = true
      try {
        let companySetting = {}
        try {
          companySetting = JSON.parse(this.company.setting)
        } catch (error) {
          console.error('Error reading company setting:', error)
        }
        taskCount++
        const formData = new FormData()
        const docName = category.key.replace(/_/g, ' ')
        formData.append("unique_id", `${this.company.name} ${docName} ${taskCount}`)
        formData.append("company_id", this.companyId)
        formData.append("task_type", taskType)
        if (password) {
          formData.append("password", password)
        }
        const companySettingsForCategory = category?.companySetting ?? []
        let text_inputs = {}
        for (const settingForCategory of companySettingsForCategory) {
          let curSettingValue = null
          if (settingForCategory == "company_name") {
            curSettingValue = this.company.name
          }
          else {
            curSettingValue = companySetting[settingForCategory]
          }
          if (curSettingValue) {
            text_inputs[settingForCategory] = curSettingValue
          }
        }
        formData.append("text_inputs", JSON.stringify(text_inputs))

        await this.$http.post(`/api/tasks/${taskType}`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        })

        alert('New task create successfully')
      } catch (error) {
        console.error('Error on creating new task:', error)
        if (error.response.status == 501) {
          alert('This feature is not yet available.')
        }
        else {
          alert('Failed to create new task')
        }
      }
      this.loading = false
      await this.fetchCompany()
    },
    async restartCategoryAllTask(category) {
      this.loading = true
      let tasksToRestart = this.company.tasks[category.key]
      if (tasksToRestart) {
        let isSuccess = true
        for (const task of tasksToRestart) {
          isSuccess &&= this.restartTask(task.id, category)
        }
        await this.fetchCompany()
        if (isSuccess) {
          alert('All Tasks restarted successfully')
        }
        else {
          alert('Not all task can be restarted.')
        }
      }
      this.loading = false
    },
    async saveField() {
      this.loading = true
      try {
        const payload = {
          [this.editFieldInfo.key]: this.editValue
        }
        await this.$http.post(`/api/company/${this.companyId}/update_setting`, { payload: JSON.stringify(payload) })
        this.companySetting[this.editFieldInfo.key] = this.editValue
        this.showEditDialog = false
        alert('Setting saved successfully')
      } catch (error) {
        console.error('Error saving setting:', error)
        alert('Failed to save setting')
      }
      this.loading = false
    },
    formatTime(time, dayOnly=false) {
      if (!time) return ''
      const date = new Date(time)
      const options = {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false
      }
      const formatter = new Intl.DateTimeFormat('en-HK', options)
      const parts = formatter.formatToParts(date)
      if (dayOnly) {
        return `${parts[4].value}-${parts[2].value}-${parts[0].value}`
      }
      return `${parts[4].value}-${parts[2].value}-${parts[0].value} ${parts[6].value}:${parts[8].value}:${parts[10].value}`
    },
    formatStatus(status) {
      if (!status) return ''
      return status
        .replace(/_/g, ' ')
        .toLowerCase()
        .split(' ')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ')
    },
    downloadSummaryUrl(category) {
      return `${this.$http.defaults.baseURL}/api/company/${this.companyId}/${category.key}/summary`
    },
    downloadTaskOutput(category, taskId) {
      if (category.key == "credit_assessment") {
        return `${this.$http.defaults.baseURL}/api/company/${this.companyId}/credit_assessment`
      }
      return `${this.$http.defaults.baseURL}/api/tasks/${taskId}/export`
    },
    getCategoryHeaders(category) {
      if (category.key === 'company_check') {
        return [
          { text: 'Document', value: 'doc_name' },
          { text: 'Progress', value: 'progress_step' },
          { text: 'Status', value: 'status' },
          { text: 'Download', value: 'download' },
        ]
      }
      if (category.key === 'credit_assessment') {
        return [
          { text: 'Document', value: 'doc_name' },
          { text: 'Status', value: 'status' },
          { text: 'Download', value: 'download' },
        ]
      }
      else {
        return [
          { text: 'Document', value: 'doc_name' },
          { text: 'Progress', value: 'progress_step' },
          { text: 'Status', value: 'status' },
          { text: 'Re-extract', value: 'reextract' },
          { text: 'Result', value: 'show_result' },
        ]
      }
    }
  },
  mounted() {
    this.companyId = this.$route.params.id
    this.fetchCompany()
    this.fetchRegions(); // Fetch regions on component mount
    // Set up interval to update progress steps every second for real-time updates
    this.updateProgressInterval = setInterval(() => {
      this.updateTaskListProgressStep();
    }, 1000); // Update every 1 second
    // Fetch tasks every 15 seconds to get new data
    this.scheduleNextFetch(15000)
  },
  beforeDestroy() {
    if (this.fetchTimer) {
      clearTimeout(this.fetchTimer)
    }
    if (this.updateProgressInterval) {
      clearInterval(this.updateProgressInterval)
    }
  }
}
</script>
